import React, { useState, useEffect, useMemo, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  propTypes,
  REVIEW_TYPE_OF_PROVIDER,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createListingCanonicalPath,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
  stringify,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { unitDivisor } from '../../util/currency';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  OFFER,
  REQUEST,
  isBookingProcess,
  isNegotiationProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  ReviewRating,
  Button,
  IconSpinner,
  Avatar,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  fetchBids,
  sendBid,
  acceptBid,
  rejectBid,
  updateBid,
  showListing,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleNavigateToMakeOfferPage,
  handleNavigateToRequestQuotePage,
  handleSubmit,
  priceForSchemaMaybe,
  createListingMetaDescription,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';
import ListingCarousel from '../PageBuilder/SectionBuilder/SectionCarousel/ListingCarousel/ListingCarousel';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard';
import { recentListingsSelector, searchRecentListings } from '../LandingPage/LandingPage.duck';
import BidModal from './BidModal/BidModal';
import UpdateBidModal from './UpdateBidModal/UpdateBidModal';
import BidUpdateSuccessModal from './BidUpdateSuccessModal/BidUpdateSuccessModal';
import FavoriteButton from './FavoriteButton';
import { recordListingView } from '../../util/api';

import css from './ListingPage.module.css';
import useViewportSize from '../../hooks/useViewportSize.js';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;
const REVIEWS_PER_PAGE = 5;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const [forceShowAllReviews, setForceShowAllReviews] = useState(false);
  const [bidModalOpen, setBidModalOpen] = useState(false);
  const [updateBidModalOpen, setUpdateBidModalOpen] = useState(false);
  const [bidToUpdate, setBidToUpdate] = useState(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successModalData, setSuccessModalData] = useState({
    previousBidAmount: 0,
    newBidAmount: 0,
  });
  const [openBidActionsTxUuid, setOpenBidActionsTxUuid] = useState(null);
  const [purchaseType, setPurchaseType] = useState(null); // 'directPurchase' or 'bidding' - will be set based on listing config
  const bidActionsMenuRef = useRef(null);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    fetchReviewsInProgress,
    sendInquiryInProgress,
    sendInquiryError,
    location,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    onRecentListingsSelector,
    recentListings,
    authorReviews,
    bids: rawBids,
    fetchBidsInProgress,
    sendBidInProgress,
    sendBidError,
    acceptBidInProgress,
    acceptBidError,
    rejectBidInProgress,
    updateBidInProgress,
    updateBidError,
    onFetchBids,
    onSendBid,
    onAcceptBid,
    onRejectBid,
    onUpdateBid,
    onRefreshListing,
    ...restOfProps
  } = props;

  const bids = Array.isArray(rawBids)
    ? rawBids.filter(
        b =>
          b?.attributes?.metadata?.is_fake !== true && b?.attributes?.metadata?.is_fake !== 'true'
      )
    : [];

  const { width } = useViewportSize();
  const forMobile = width < 1024;

  const currentUserId = currentUser?.id?.uuid;

  // Helper: returns true if a bid belongs to the current user and is active (pending/accepted)
  const isOwnActiveBid = b => {
    const pd = b?.attributes?.protectedData || {};
    const bidderUuid = b?.customer?.id?.uuid || b?.relationships?.customer?.data?.id?.uuid;
    return (
      pd?.isBidTransaction &&
      (pd?.bidStatus === 'pending' || pd?.bidStatus === 'accepted') &&
      bidderUuid &&
      bidderUuid === currentUserId
    );
  };

  // User has a real (non-fake) active bid → always blocks new bids
  const hasRealActiveBid =
    Array.isArray(rawBids) &&
    rawBids.some(b => {
      const metadata = b?.attributes?.metadata || {};
      const isFake = metadata.is_fake === true || metadata.is_fake === 'true';
      return !isFake && isOwnActiveBid(b);
    });

  // User has a fake bid WITHOUT allow_rebid → blocks new bids
  const hasBlockingFakeBid =
    Array.isArray(rawBids) &&
    rawBids.some(b => {
      const metadata = b?.attributes?.metadata || {};
      const isFake = metadata.is_fake === true || metadata.is_fake === 'true';
      const allowRebid = metadata.allow_rebid === true || metadata.allow_rebid === 'true';
      return isFake && !allowRebid && isOwnActiveBid(b);
    });

  // User has a fake bid WITH allow_rebid → does NOT block new bids
  const hasFakeBidWithRebid =
    Array.isArray(rawBids) &&
    rawBids.some(b => {
      const metadata = b?.attributes?.metadata || {};
      const isFake = metadata.is_fake === true || metadata.is_fake === 'true';
      const allowRebid = metadata.allow_rebid === true || metadata.allow_rebid === 'true';
      return isFake && allowRebid && isOwnActiveBid(b);
    });

  // True when the user is blocked from placing a new bid (real bid, or fake without rebid)
  const hasActiveBid = hasRealActiveBid || hasBlockingFakeBid;

  // True when the user should see the "flagged" warning (blocked fake bid, no real bid)
  const hasFakeBid = !hasRealActiveBid && hasBlockingFakeBid;

  // True when the user should see the "rebid allowed" notice alongside the "Make a bid" button
  const canRebidAfterFake = !hasRealActiveBid && !hasBlockingFakeBid && hasFakeBidWithRebid;

  useEffect(() => {
    setMounted(true);
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (location?.state?.forceScrollTop) {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      setTimeout(() => window.scrollTo(0, 0), 120);
    }
  }, [location?.key, location?.state?.forceScrollTop]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [rawParams.id]);

  useEffect(() => {
    if (!openBidActionsTxUuid) return;

    const handleDocumentMouseDown = event => {
      const el = bidActionsMenuRef.current;
      if (!el) return;
      if (el.contains(event.target)) return;
      setOpenBidActionsTxUuid(null);
    };

    const handleDocumentKeyDown = event => {
      if (event.key === 'Escape') {
        setOpenBidActionsTxUuid(null);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [openBidActionsTxUuid]);

  useEffect(() => {
    if (onRecentListingsSelector && rawParams?.id) {
      const excludeId = rawParams?.id;
      onRecentListingsSelector({ config, excludeId });
    }
  }, [onRecentListingsSelector, config, rawParams?.id]);

  // Initialize purchaseType based on listing configuration
  // This must be at the top level with other hooks to avoid "rendered more hooks" error
  useEffect(() => {
    // We need to get the listing data to determine the purchase type
    const listingId = new UUID(rawParams.id);
    const listing =
      rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT ||
      rawParams.variant === LISTING_PAGE_DRAFT_VARIANT ||
      showOwnListingsOnly
        ? getOwnListing(listingId)
        : getListing(listingId);

    const publicData = listing?.attributes?.publicData || {};
    const { biddingEnabled, fixedPriceEnabled } = publicData;

    // Only set default purchaseType if it hasn't been explicitly set by user (still null)
    // This prevents resetting the user's selection when they click the radio button
    setPurchaseType(prev => {
      if (prev !== null) {
        // User has already made a selection, don't override it
        return prev;
      }
      // When both are enabled, default to bidding
      // When only one is enabled, use that one
      if (biddingEnabled && fixedPriceEnabled) {
        return 'bidding';
      } else if (biddingEnabled) {
        return 'bidding';
      } else if (fixedPriceEnabled) {
        return 'directPurchase';
      }
      return null;
    });
  }, [rawParams.id, rawParams.variant, showOwnListingsOnly, getListing, getOwnListing]);

  // Log when purchaseType changes
  useEffect(() => {
    // purchaseType changed
  }, [purchaseType]);

  // "Customers Reviews" should reflect the seller's reviews (reviews about provider),
  // not reviews about this specific listing.
  const providerAuthorReviews = useMemo(
    () =>
      (Array.isArray(authorReviews) ? authorReviews : []).filter(
        r => r?.attributes?.type === REVIEW_TYPE_OF_PROVIDER
      ),
    [authorReviews]
  );
  const customerReviewsList = providerAuthorReviews;
  const authorReviewCount = providerAuthorReviews.length;
  const authorAverageRating = authorReviewCount
    ? providerAuthorReviews.reduce((sum, r) => sum + (r?.attributes?.rating || 0), 0) /
      authorReviewCount
    : 0;
  const summaryCount = authorReviewCount;
  const summaryRounded = Math.round(authorAverageRating || 0);

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));
  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  // Record a listing view (throttled per session) when the listing is loaded.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isDraftVariant || isPendingApprovalVariant) return;

    const listingUuid = currentListing?.id?.uuid;
    if (!listingUuid) return;

    const viewerUuid = currentUser?.id?.uuid;
    const ownerUuid = currentListing?.author?.id?.uuid;
    if (viewerUuid && ownerUuid && viewerUuid === ownerUuid) return;

    const throttleMs = 30 * 60 * 1000;
    const storageKey = `listingViewed:${listingUuid}`;
    const now = Date.now();

    try {
      const last = window.sessionStorage.getItem(storageKey);
      if (last && now - Number(last) < throttleMs) return;
      window.sessionStorage.setItem(storageKey, String(now));
    } catch (e) {
      // Ignore storage access errors (private mode, blocked storage, etc.)
    }

    recordListingView({ listingId: listingUuid }).catch(() => {});
  }, [
    currentListing?.id?.uuid,
    currentListing?.author?.id?.uuid,
    currentUser?.id?.uuid,
    isDraftVariant,
    isPendingApprovalVariant,
  ]);
  // Parse current query for pagination
  const searchParams = useMemo(() => parse(location.search) || {}, [location.search]);
  const { page: reviewsPage, ...baseSearchParams } = searchParams;
  const currentReviewsPage = Number(reviewsPage) || 1;
  const showAllReviews = forceShowAllReviews || Boolean(reviewsPage);

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;
  const listings = useMemo(() => {
    const base = Array.isArray(recentListings) ? recentListings : [];
    const currentAuthorId = currentListing?.author?.id?.uuid;
    return currentAuthorId ? base.filter(l => l?.author?.id?.uuid === currentAuthorId) : base;
  }, [recentListings, currentListing]);
  const slidesToShow = 4;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  // Redirect to NotFoundPage if listing is soft-deleted
  const isDeleted = Boolean(currentListing?.attributes?.publicData?.status === 'deleted');
  if (isDeleted) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
    ? 'purchase'
    : isNegotiation
    ? 'negotiation'
    : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const shippingAddress = ensuredAuthor?.attributes?.profile?.publicData?.shippingAddress || null;
  const city = shippingAddress?.city || '';
  const cc = (shippingAddress?.cc || '').toUpperCase();
  let countryName = '';
  try {
    if (cc && typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
      const displayNames = new Intl.DisplayNames([intl?.locale || 'en'], { type: 'region' });
      countryName = displayNames.of(cc) || cc;
    } else {
      countryName = cc || '';
    }
  } catch (_) {
    countryName = cc || '';
  }
  const locationText = [city, countryName].filter(Boolean).join(', ');
  const authorNeedsPayoutDetails =
    ['booking', 'purchase'].includes(processType) || (isNegotiation && unitType === OFFER);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && authorNeedsPayoutDetails && !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquire transition to inquiry state in booking, purchase and negotiation processes.
  // Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToMakeOfferPage = handleNavigateToMakeOfferPage({
    ...commonParams,
    getListing,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToRequestQuotePage = handleNavigateToRequestQuotePage({
    ...commonParams,
    getListing,
  });
  const onSubmit = values => {
    return handleSubmit({
      ...commonParams,
      currentUser,
      callSetInitialValues,
      getListing,
      onInitializeCardPaymentData,
    })({
      ...values,
      providerCart: {
        [listingId.uuid]: {
          quantity: values.quantity ?? 1,
        },
      },
    });
  };

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;

    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
      return;
    }

    // If a bid has been accepted, only the accepted bidder can proceed to checkout
    if (publicData?.listingOpenStatus === 'pending') {
      const currentUserId = currentUser?.id?.uuid;
      const acceptedBid = Array.isArray(bids)
        ? bids.find(bid => {
            const bidderUuid =
              bid?.customer?.id?.uuid || bid?.relationships?.customer?.data?.id?.uuid;
            return (
              bidderUuid === currentUserId &&
              bid?.attributes?.lastTransition === 'transition/accept-bid'
            );
          })
        : null;

      if (!acceptedBid) {
        // Not the accepted bidder — silently block (OrderPanel already shows disabled state)
        return;
      }

      // Accepted bidder — proceed to checkout with the existing transaction
      const callSetInitialValuesWithTx = (
        setInitialValues,
        initialValues,
        saveToSessionStorage
      ) => {
        callSetInitialValues(
          setInitialValues,
          { ...initialValues, transaction: acceptedBid },
          saveToSessionStorage
        );
      };
      return handleSubmit({
        ...commonParams,
        currentUser,
        callSetInitialValues: callSetInitialValuesWithTx,
        getListing,
        onInitializeCardPaymentData,
      })({
        ...values,
        providerCart: { [listingId.uuid]: { quantity: values.quantity ?? 1 } },
      });
    }

    if (isNegotiation && unitType === REQUEST) {
      onNavigateToMakeOfferPage(values);
    } else if (isNegotiation && unitType === OFFER) {
      onNavigateToRequestQuotePage(values);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  const canonicalPath = createListingCanonicalPath(title, currentListing.id);
  const metaDescription = createListingMetaDescription({
    title,
    description,
    publicData,
    intl,
  });
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${canonicalPath}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const isOutOfStock = currentStock <= 0;

  const isBothEnabled = publicData?.biddingEnabled && publicData?.fixedPriceEnabled;
  const isBootleg =
    publicData?.categoryLevel2 && publicData.categoryLevel2.toLowerCase().includes('bootleg');
  const adjustedStock = currentStock;

  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};
  const noIndexMaybe =
    currentListing.attributes.state === LISTING_STATE_CLOSED ? { noIndex: true } : {};
  const onShowAllReviews = () => {
    const pathParams = params;
    const searchParamsWithPage = { ...baseSearchParams, page: 1 };
    const url = createResourceLocatorString(
      'ListingReviewsPage',
      routeConfiguration,
      pathParams,
      searchParamsWithPage
    );
    history.push(url);
  };

  return (
    <Page
      canonicalPath={canonicalPath}
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={metaDescription}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      {...noIndexMaybe}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: metaDescription,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForProductLayout}>
            {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                currentUser={currentUser}
              />
            ) : null}
            {mounted && currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                currentUser={currentUser}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null}
            {showListingImage && (
              <SectionGallery
                listing={currentListing}
                variantPrefix={config.layout.listingImage.variantPrefix}
                favoriteControl={
                  !isOwnListing && currentListing.attributes.state !== LISTING_STATE_CLOSED ? (
                    <FavoriteButton
                      listing={currentListing}
                      isAuthenticated={isAuthenticated}
                      onManageDisableScrolling={onManageDisableScrolling}
                    />
                  ) : null
                }
              />
            )}
            {/* <div
              className={showListingImage ? css.mobileHeading : css.noListingImageHeadingProduct}
            >
              {showListingImage ? (
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              ) : (
                <H3 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H3>
              )}
            </div> */}
            {showListingImage && (
              <div className={css.orderHeading}>
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
                {formattedPrice && <div className={css.priceBelowTitle}>{formattedPrice}</div>}

                {publicData?.biddingEnabled && publicData?.listingOpenStatus !== 'pending' && (
                  <div className={css.biddingAvailableBadge}>
                    <FormattedMessage id="ListingPage.biddingAvailableBadge" />
                  </div>
                )}

                {city ? (
                  <div
                    className={classNames(
                      css.locationText,
                      publicData?.biddingEnabled && publicData && css.extraspace
                    )}
                  >
                    <IconCard brand="location" />
                    {locationText}
                  </div>
                ) : null}

                {isBootleg ? <div className={css.bootlegTag}>Bootleg</div> : null}
              </div>
            )}

            <div className={css.description}>
              <SectionTextMaybe text={description} showAsIngress />
            </div>

            {/* {(showAllReviews || reviews.length <= 5) ? <div>
              <SectionReviews
                reviews={reviews}
                fetchReviewsError={fetchReviewsError}
                fetchReviewsInProgress={fetchReviewsInProgress}
                page={currentReviewsPage}
                perPage={REVIEWS_PER_PAGE}
                pageName={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                pagePathParams={params}
                pageSearchParams={baseSearchParams}
              />
            </div> : null} */}

            <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
            />

            <div>
              {fetchReviewsInProgress && !Array.isArray(authorReviews) ? (
                <IconSpinner />
              ) : (
                <>
                  <h4 className={css.reviewHeaidng}>
                    <FormattedMessage id="ListingPage.customerReviewsTitle" />
                  </h4>
                  <div className={css.reviewSection}>
                    <div className={css.reviewsSummaryCard}>
                      <div
                        className={classNames(
                          css.reviewsSummaryTop,
                          customerReviewsList.length > 0
                            ? css.reviewsSummaryWithButton
                            : css.reviewsSummaryWithoutButton
                        )}
                      >
                        <div className={css.reviewsSummaryCol}>
                          <div className={css.reviewsSummaryLabel}>
                            <FormattedMessage id="ListingPage.totalReviewsHeader" />
                          </div>
                          <div className={css.reviewsSummaryValue}>{summaryCount}</div>
                          <div className={css.reviewsSummaryHelp}>
                            <FormattedMessage id="ListingPage.totalReviewsHelp" />
                          </div>
                        </div>

                        <div className={css.reviewsSummaryDivider} />

                        <div className={css.reviewsSummaryCol}>
                          <div className={css.reviewsSummaryLabel}>
                            <FormattedMessage id="ListingPage.averageRatingHeader" />
                          </div>
                          <div className={css.row}>
                            <div className={css.reviewsSummaryValue}>{summaryRounded}</div>
                            <ReviewRating
                              rating={summaryRounded}
                              className={css.reviewsSummaryStars}
                              reviewStarClassName={css.reviewsSummaryStar}
                            />
                          </div>

                          <div className={css.reviewCount}>
                            <div>
                              <FormattedMessage
                                id="ListingPage.reviewsSummaryTotal"
                                values={{ count: customerReviewsList.length }}
                              />
                            </div>
                          </div>
                        </div>

                        {!showAllReviews &&
                        Array.isArray(customerReviewsList) &&
                        customerReviewsList.length > 0 ? (
                          <Button
                            type="button"
                            onClick={onShowAllReviews}
                            className={css.reviewsSummaryButton}
                          >
                            <FormattedMessage id="ListingPage.showAllReviews" />
                          </Button>
                        ) : null}

                        {fetchReviewsInProgress && !Array.isArray(authorReviews) ? (
                          <div className={css.reviewsSummarySpinner}>
                            <IconSpinner />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            />
          </div>
          <div className={css.orderColumnForProductLayout}>
            {/* Purchase type selector - only show when both bidding and fixed price are enabled */}
            {publicData?.biddingEnabled && publicData?.fixedPriceEnabled && (
              <div className={css.purchaseTypeSelector}>
                <div className={css.purchaseTypeOptions}>
                  {/* Direct Purchase Card */}
                  <button
                    type="button"
                    className={classNames(css.buyitBtn, css.purchaseTypeCard, {
                      [css.purchaseTypeCardActive]: purchaseType === 'directPurchase',
                    })}
                    onClick={() => {
                      setPurchaseType('directPurchase');
                      // Open the modal when switching to direct purchase
                      const { pathname, search, state } = location;
                      const newSearch = `?${stringify({ ...parse(search), orderOpen: true })}`;
                      history.push(`${pathname}${newSearch}`, state);
                    }}
                  >
                    <div className={css.purchaseTypeCardContent}>
                      <div>
                        <h3 className={css.purchaseTypeCardTitle}>
                          <FormattedMessage id="OrderPanel.directPurchaseTitle" />
                        </h3>
                        <p className={css.purchaseTypeCardSubtitle}>
                          <FormattedMessage id="OrderPanel.directPurchaseSubtitle" />
                        </p>
                      </div>
                      {currentListing?.attributes?.price && (
                        <p className={css.purchaseTypeCardPrice}>
                          {intl.formatNumber(currentListing.attributes.price.amount / 100, {
                            style: 'currency',
                            currency: currentListing.attributes.price.currency,
                          })}
                        </p>
                      )}
                    </div>
                  </button>

                  {/* OR Divider */}
                  <div className={css.purchaseTypeOrDivider}>
                    <span className={css.purchaseTypeOrText}>
                      <FormattedMessage id="OrderPanel.orDivider" />
                    </span>
                  </div>

                  {/* Bidding Card */}
                  <button
                    type="button"
                    className={classNames(css.makeABid, css.purchaseTypeCard, {
                      [css.purchaseTypeCardActive]:
                        purchaseType === 'bidding' ||
                        (purchaseType === null && publicData?.biddingEnabled),
                    })}
                    onClick={() => {
                      setPurchaseType('bidding');
                      // Close the modal when switching to bidding
                      const { pathname, search, state } = location;
                      const { orderOpen, ...searchParams } = parse(search);
                      const searchString = `?${stringify(searchParams)}`;
                      history.push(`${pathname}${searchString}`, state);

                      console.log(hasActiveBid, '>>>hasActiveBid');
                      if (forMobile && !hasActiveBid) {
                        setBidModalOpen(true);
                      } else {
                        const el = document.getElementById('biddingPanel');

                        if (!el) {
                          console.error("Element with id 'biddingPanel' not found!");
                          return;
                        }

                        // Option 2: More robust (recommended for complex layouts)
                        setTimeout(() => {
                          el.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest',
                          });
                        }, 400);
                      }
                    }}
                  >
                    <div className={css.purchaseTypeCardContent}>
                      <div className={css.purchaseTypeCardHeader}>
                        <div>
                          <h3 className={css.purchaseTypeCardTitle}>
                            <FormattedMessage id="OrderPanel.biddingTitle" />
                          </h3>
                          {/* <svg className={css.purchaseTypeCardIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm0 0c-1.657 0-3 1.343-3 3v6c0 1.657 1.343 3 3 3s3-1.343 3-3v-6c0-1.657-1.343-3-3-3z" />
                          </svg> */}
                          <p className={css.purchaseTypeCardSubtitle}>
                            <FormattedMessage id="OrderPanel.biddingSubtitle" />
                          </p>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {currentListing?.id?.uuid ? (
              <div className={css.stickyOrderPanel} id="makeabidcontainer">
                {(() => {
                  const isBidPending = publicData?.listingOpenStatus === 'pending';
                  const currentUserId = currentUser?.id?.uuid;
                  const acceptedBid = Array.isArray(bids)
                    ? bids.find(bid => {
                        const bidderUuid =
                          bid?.customer?.id?.uuid || bid?.relationships?.customer?.data?.id?.uuid;
                        const protectedData = bid?.attributes?.protectedData || {};
                        const lastTransition = bid?.attributes?.lastTransition || '';
                        const bidStatus =
                          lastTransition === 'transition/cancel-bid-expire' ||
                          lastTransition === 'transition/expire-payment' ||
                          lastTransition === 'transition/expire-bid-payment'
                            ? 'expired'
                            : protectedData.bidStatus ||
                              (lastTransition === 'transition/accept-bid' ? 'accepted' : '');
                        const bidPaymentExpiresAt =
                          protectedData.bidPaymentExpiresAt || protectedData.couponExpiresAt;
                        const isExpired =
                          bidStatus === 'accepted' &&
                          bidPaymentExpiresAt &&
                          new Date(bidPaymentExpiresAt) < new Date();
                        return (
                          bidderUuid === currentUserId && bidStatus === 'accepted' && !isExpired
                        );
                      })
                    : null;
                  const isAcceptedBidder = !!acceptedBid;

                  // Accepted bidder: show a dedicated "Complete purchase" button
                  // that goes directly to the existing transaction page
                  if (isAcceptedBidder && !isOwnListing) {
                    const txId = acceptedBid?.id?.uuid;
                    return (
                      <div className={css.acceptedBidPanel}>
                        <div className={css.acceptedBidInfo}>
                          <FormattedMessage id="ListingPage.acceptedBidInfo" />
                        </div>
                        <NamedLink
                          name="OrderDetailsPage"
                          params={{ id: txId }}
                          className={css.completePurchaseButton}
                        >
                          <FormattedMessage id="ListingPage.completePurchase" />
                        </NamedLink>
                      </div>
                    );
                  }

                  // Normal flow: show OrderPanel or BiddingPanel
                  // If both bidding and fixed price are enabled, show based on purchaseType selection
                  const showBiddingPanel =
                    publicData?.biddingEnabled && purchaseType !== 'directPurchase';

                  if (showBiddingPanel) {
                    const isBidPending = publicData?.listingOpenStatus === 'pending';

                    // Find highest pending bid. Accepted bids already hold stock and leave bidding.
                    const activeBids = Array.isArray(bids)
                      ? bids.filter(b => {
                          const pd = b?.attributes?.protectedData || {};
                          const lt = b?.attributes?.lastTransition || '';
                          const status =
                            lt === 'transition/cancel-bid-expire' ||
                            lt === 'transition/expire-payment' ||
                            lt === 'transition/expire-bid-payment'
                              ? 'expired'
                              : pd.bidStatus ||
                                (lt === 'transition/accept-bid'
                                  ? 'accepted'
                                  : lt === 'transition/reject-bid'
                                  ? 'rejected'
                                  : 'pending');
                          return status === 'pending';
                        })
                      : [];
                    const sortedBids = Array.isArray(activeBids)
                      ? [...activeBids].sort((a, b) => {
                          const aAmt = a?.attributes?.protectedData?.bidAmountInSubunits || 0;
                          const bAmt = b?.attributes?.protectedData?.bidAmountInSubunits || 0;
                          return bAmt - aAmt;
                        })
                      : [];
                    const highestBid = sortedBids[0] || null;
                    const highestBidAmount =
                      highestBid?.attributes?.protectedData?.bidAmountInSubunits;
                    const highestBidCurrency =
                      highestBid?.attributes?.protectedData?.currency || config.currency;
                    const highestBidderName =
                      highestBid?.customer?.attributes?.profile?.displayName || '—';
                    const formattedHighest =
                      highestBidAmount != null
                        ? intl.formatNumber(highestBidAmount / 100, {
                            style: 'currency',
                            currency: highestBidCurrency,
                          })
                        : null;

                    // Sort all bids (including rejected) for display
                    const allBidsSorted = Array.isArray(bids)
                      ? [...bids].sort((a, b) => {
                          const aAmt = a?.attributes?.protectedData?.bidAmountInSubunits || 0;
                          const bAmt = b?.attributes?.protectedData?.bidAmountInSubunits || 0;
                          return bAmt - aAmt;
                        })
                      : [];

                    const formatRelativeTime = date => {
                      if (!date) return '—';
                      const d = new Date(date);
                      const now = new Date();
                      const diffMs = now - d;
                      const diffMins = Math.floor(diffMs / 60000);
                      const diffHours = Math.floor(diffMins / 60);
                      const diffDays = Math.floor(diffHours / 24);
                      const timeStr = d.toLocaleTimeString(intl.locale, {
                        hour: '2-digit',
                        minute: '2-digit',
                      });
                      if (diffDays > 0)
                        return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago · ${timeStr}`;
                      if (diffHours > 0) return `${diffHours}h ago · ${timeStr}`;
                      return `${diffMins}m ago · ${timeStr}`;
                    };

                    return (
                      <div id="biddingPanel" className={css.biddingPanel}>
                        {/* Highest bid card */}
                        {formattedHighest && (
                          <div className={css.highestBidCard}>
                            <div className={css.highestBidLeft}>
                              <span className={css.highestBidLabel}>
                                <FormattedMessage id="ListingPage.highestBid" />
                              </span>
                              <span className={css.highestBidBy}>
                                <FormattedMessage
                                  id="ListingPage.highestBidBy"
                                  values={{ name: highestBidderName }}
                                />
                              </span>
                            </div>
                            <span className={css.highestBidAmount}>{formattedHighest}</span>
                          </div>
                        )}

                        {/* Bid list */}
                        <div className={css.biddingPanelList}>
                          <div className={css.biddingPanelListHeader}>
                            <span className={css.biddingPanelListTitle}>
                              <FormattedMessage id="ListingPage.biddingSection" />
                              {bids.length > 0 && (
                                <span className={css.bidCount}>{bids.length}</span>
                              )}
                            </span>
                            {bids.length > 0 && (
                              <span className={css.biddingPanelTotalBids}>
                                <FormattedMessage
                                  id="ListingPage.totalBids"
                                  values={{ count: bids.length }}
                                />
                              </span>
                            )}
                          </div>

                          {fetchBidsInProgress ? (
                            <div className={css.biddingPanelSpinner}>
                              <IconSpinner />
                            </div>
                          ) : bids.length === 0 ? (
                            <div className={css.noBidsBox}>
                              <p className={css.noBids}>
                                <FormattedMessage id="ListingPage.noBids" />
                              </p>
                            </div>
                          ) : (
                            <div className={css.biddingPanelBidList}>
                              {allBidsSorted.map((bid, idx) => {
                                const txId = bid?.id;
                                const txUuid = txId?.uuid;
                                const isOptimisticTx =
                                  typeof txUuid === 'string' && txUuid.startsWith('optimistic-');
                                const bidAmount =
                                  bid?.attributes?.protectedData?.bidAmountInSubunits;
                                const bidCurrency =
                                  bid?.attributes?.protectedData?.currency || config.currency;
                                const bidderName =
                                  bid?.customer?.attributes?.profile?.displayName || '—';
                                const bidderUuid =
                                  bid?.customer?.id?.uuid ||
                                  bid?.relationships?.customer?.data?.id?.uuid;
                                const currentUserId = currentUser?.id?.uuid;
                                const isOwnBid =
                                  !!currentUserId && !!bidderUuid && bidderUuid === currentUserId;
                                const bidTime = bid?.attributes?.lastTransitionedAt;
                                const lastTransition = bid?.attributes?.lastTransition || '';
                                const protectedData = bid?.attributes?.protectedData || {};
                                const bidStatus =
                                  lastTransition === 'transition/cancel-bid-expire' ||
                                  lastTransition === 'transition/expire-payment' ||
                                  lastTransition === 'transition/expire-bid-payment'
                                    ? 'expired'
                                    : protectedData.bidStatus ||
                                      (lastTransition === 'transition/accept-bid'
                                        ? 'accepted'
                                        : lastTransition === 'transition/reject-bid'
                                        ? 'rejected'
                                        : 'pending');
                                const bidPaymentExpiresAt =
                                  protectedData.bidPaymentExpiresAt ||
                                  protectedData.couponExpiresAt;
                                const promoExpiry = bidPaymentExpiresAt
                                  ? new Date(bidPaymentExpiresAt)
                                  : null;
                                const isExpired =
                                  bidStatus === 'accepted' &&
                                  promoExpiry &&
                                  promoExpiry < new Date();
                                const effectiveStatus = isExpired ? 'expired' : bidStatus;
                                const isHighest = idx === 0;
                                const formattedBid =
                                  bidAmount != null
                                    ? intl.formatNumber(bidAmount / 100, {
                                        style: 'currency',
                                        currency: bidCurrency,
                                      })
                                    : '—';
                                const isAccepting = acceptBidInProgress === txId;
                                const isRejecting = rejectBidInProgress === txId;
                                const isAccepted = effectiveStatus === 'accepted';
                                const canAcceptBid =
                                  effectiveStatus === 'pending' && currentStock > 0;
                                const isActionsOpen = !!txUuid && openBidActionsTxUuid === txUuid;
                                const canNavigateToBidTx =
                                  !!txUuid &&
                                  !isOptimisticTx &&
                                  routeConfiguration &&
                                  (isOwnListing || (!isOwnListing && isAuthenticated && isOwnBid));
                                const bidTxPath = canNavigateToBidTx
                                  ? createResourceLocatorString(
                                      isOwnListing ? 'SaleDetailsPage' : 'OrderDetailsPage',
                                      routeConfiguration,
                                      { id: txUuid }
                                    )
                                  : null;
                                // Show accept error only on the row that failed
                                const rowAcceptError =
                                  acceptBidError?.txUuid &&
                                  txUuid &&
                                  acceptBidError.txUuid === txUuid
                                    ? acceptBidError.message
                                    : null;

                                return (
                                  <div
                                    key={txUuid}
                                    className={classNames(css.biddingPanelBidRowWrapper, {
                                      [css.biddingPanelBidRowWrapperClickable]: !!bidTxPath,
                                    })}
                                    role={bidTxPath ? 'button' : undefined}
                                    tabIndex={bidTxPath ? 0 : undefined}
                                    onClick={() => {
                                      if (!bidTxPath) return;
                                      history.push(bidTxPath);
                                    }}
                                    onKeyDown={e => {
                                      if (!bidTxPath) return;
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        history.push(bidTxPath);
                                      }
                                    }}
                                  >
                                    <div
                                      className={css.biddingPanelBidRow}
                                      ref={isActionsOpen ? bidActionsMenuRef : null}
                                    >
                                      <Avatar user={bid?.customer} disableProfileLink />

                                      <div className={css.biddingPanelBidderInfo}>
                                        <span className={css.biddingPanelBidderName}>
                                          {bidderName}
                                        </span>
                                        {/* <span className={css.biddingPanelBidTime}>
                                          {formatRelativeTime(bidTime)}
                                        </span> */}
                                      </div>
                                      <div className={css.biddingPanelBidRight}>
                                        <span className={css.biddingPanelBidAmount}>
                                          {formattedBid}
                                        </span>
                                        {effectiveStatus === 'accepted' ? (
                                          <span className={css.biddingPanelStatusAccepted}>
                                            <FormattedMessage id="ListingPage.bidStatusAccepted" />
                                          </span>
                                        ) : effectiveStatus === 'rejected' ? (
                                          <span className={css.biddingPanelStatusRejected}>
                                            <FormattedMessage id="ListingPage.bidStatusRejected" />
                                          </span>
                                        ) : effectiveStatus === 'expired' ? (
                                          <span className={css.biddingPanelStatusExpired}>
                                            <FormattedMessage id="ListingPage.bidStatusExpired" />
                                          </span>
                                        ) : isHighest ? (
                                          <span className={css.biddingPanelStatusHighest}>
                                            <FormattedMessage id="ListingPage.bidStatusHighest" />
                                          </span>
                                        ) : (
                                          <span className={css.biddingPanelStatusPending}>
                                            <FormattedMessage id="ListingPage.bidStatusPending" />
                                          </span>
                                        )}
                                      </div>
                                      {isOwnListing &&
                                        !isAccepted &&
                                        !isBidPending &&
                                        effectiveStatus !== 'rejected' && (
                                          <div className={css.bidActionsMenu}>
                                            <button
                                              type="button"
                                              className={css.bidActionsMenuButton}
                                              aria-haspopup="menu"
                                              aria-expanded={isActionsOpen}
                                              aria-label="Bid actions"
                                              disabled={isAccepting || isRejecting || !canAcceptBid}
                                              onClick={e => {
                                                e.stopPropagation();
                                                setOpenBidActionsTxUuid(prev =>
                                                  prev === txUuid ? null : txUuid
                                                );
                                              }}
                                            >
                                              <svg
                                                className={css.bidActionsMenuIcon}
                                                width="4"
                                                height="16"
                                                viewBox="0 0 4 16"
                                                xmlns="http://www.w3.org/2000/svg"
                                                aria-hidden="true"
                                                focusable="false"
                                              >
                                                <circle cx="2" cy="2" r="1" />
                                                <circle cx="2" cy="8" r="1" />
                                                <circle cx="2" cy="14" r="1" />
                                              </svg>
                                            </button>
                                          </div>
                                        )}
                                      {isOwnListing &&
                                        !isAccepted &&
                                        !isBidPending &&
                                        effectiveStatus === 'rejected' && (
                                          <div className={css.bidActionsMenuPlaceholder} />
                                        )}
                                      {isOwnListing &&
                                        !isAccepted &&
                                        !isBidPending &&
                                        effectiveStatus !== 'rejected' && (
                                          <div
                                            className={classNames(css.biddingPanelHoverActions, {
                                              [css.biddingPanelHoverActionsOpen]: isActionsOpen,
                                            })}
                                          >
                                            <button
                                              type="button"
                                              className={css.biddingPanelAcceptBtn}
                                              disabled={isAccepting || isRejecting}
                                              onClick={e => {
                                                e.stopPropagation();
                                                setOpenBidActionsTxUuid(null);

                                                onAcceptBid(txId, listingId)
                                                  .then(() =>
                                                    Promise.all([
                                                      onFetchBids(listingId),
                                                      onRefreshListing(listingId, config),
                                                    ])
                                                  )
                                                  .catch(() => {});
                                              }}
                                            >
                                              {isAccepting ? (
                                                '…'
                                              ) : (
                                                <FormattedMessage id="ListingPage.bidAccept" />
                                              )}
                                            </button>
                                            <button
                                              type="button"
                                              className={css.biddingPanelDeclineBtn}
                                              disabled={isAccepting || isRejecting}
                                              onClick={e => {
                                                e.stopPropagation();
                                                setOpenBidActionsTxUuid(null);
                                                onRejectBid(txId).catch(() => {});
                                              }}
                                            >
                                              {isRejecting ? (
                                                '…'
                                              ) : (
                                                <FormattedMessage id="ListingPage.bidReject" />
                                              )}
                                            </button>
                                          </div>
                                        )}
                                    </div>
                                    {rowAcceptError && (
                                      <div className={css.biddingPanelAcceptError}>
                                        {rowAcceptError}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Edit bid button - shown below bids list if user has an editable bid */}
                        {(() => {
                          if (isOwnListing || !isAuthenticated || isBidPending) return null;

                          // Find user's editable bid
                          const userEditableBid = bids.find(bid => {
                            const bidderUuid =
                              bid?.customer?.id?.uuid ||
                              bid?.relationships?.customer?.data?.id?.uuid;
                            const isOwnBid = bidderUuid === currentUserId;

                            if (!isOwnBid) return false;

                            const lastTransition = bid?.attributes?.lastTransition || '';
                            const protectedData = bid?.attributes?.protectedData || {};
                            const bidStatus =
                              lastTransition === 'transition/cancel-bid-expire' ||
                              lastTransition === 'transition/expire-payment' ||
                              lastTransition === 'transition/expire-bid-payment'
                                ? 'expired'
                                : protectedData.bidStatus ||
                                  (lastTransition === 'transition/accept-bid'
                                    ? 'accepted'
                                    : lastTransition === 'transition/reject-bid'
                                    ? 'rejected'
                                    : 'pending');

                            // Check if this is the highest bid by comparing with all active bids
                            const userBidAmount = protectedData.bidAmountInSubunits || 0;
                            const allActiveBidAmounts = bids
                              .map(b => b?.attributes?.protectedData)
                              .filter(pd => pd?.isBidTransaction && pd?.bidStatus === 'pending')
                              .map(pd => pd?.bidAmountInSubunits)
                              .filter(a => typeof a === 'number');

                            const highestBidAmount = allActiveBidAmounts.length
                              ? Math.max(...allActiveBidAmounts)
                              : 0;

                            const isHighest = userBidAmount >= highestBidAmount;

                            return (
                              bidStatus === 'pending' &&
                              !isHighest &&
                              lastTransition !== 'transition/accept-bid' &&
                              lastTransition !== 'transition/reject-bid'
                            );
                          });

                          if (!userEditableBid) return null;

                          const isCurrentlyClosed =
                            currentListing.attributes.state === LISTING_STATE_CLOSED;

                          return (
                            <button
                              type="button"
                              className={css.biddingPanelMakeBidBtn}
                              onClick={() => {
                                if (!isCurrentlyClosed) {
                                  setBidToUpdate(userEditableBid);
                                  setUpdateBidModalOpen(true);
                                }
                              }}
                              disabled={isCurrentlyClosed}
                              style={
                                isCurrentlyClosed
                                  ? { opacity: 0.5, cursor: 'not-allowed', marginTop: '12px' }
                                  : { marginTop: '12px' }
                              }
                            >
                              <FormattedMessage id="ListingPage.editBidButton" />
                            </button>
                          );
                        })()}

                        {/* Make a bid button */}
                        {!isOwnListing && isAuthenticated && !isBidPending && !hasActiveBid && (
                          <>
                            <button
                              type="button"
                              className={css.biddingPanelMakeBidBtn}
                              onClick={() => {
                                const isCurrentlyClosed =
                                  currentListing.attributes.state === LISTING_STATE_CLOSED;
                                if (!isCurrentlyClosed && !isOutOfStock) {
                                  setBidModalOpen(true);
                                }
                              }}
                              disabled={
                                currentListing.attributes.state === LISTING_STATE_CLOSED ||
                                isOutOfStock
                              }
                            >
                              <FormattedMessage id="ListingPage.makeBid" />
                            </button>
                            {isOutOfStock ? (
                              <div className={css.biddingPanelOutOfStockNotice}>
                                <FormattedMessage id="ListingPage.biddingOutOfStock" />
                              </div>
                            ) : null}
                          </>
                        )}
                        {/* Rebid allowed notice – shown when the flagged bid has allow_rebid */}
                        {!isOwnListing && isAuthenticated && !isBidPending && canRebidAfterFake && (
                          <div className={css.biddingPanelRebidNotice}>
                            <FormattedMessage
                              id="ListingPage.fakeBidRebidNotice"
                              values={{
                                supportEmail: (
                                  <a
                                    href="mailto:support@groovebay.eu"
                                    className={css.rebidNoticeEmail}
                                  >
                                    support@groovebay.eu
                                  </a>
                                ),
                              }}
                            />
                          </div>
                        )}
                        {/* Fake bid warning – shown when the flagged bid does NOT have allow_rebid */}
                        {!isOwnListing && isAuthenticated && !isBidPending && hasFakeBid && (
                          <div className={css.biddingPanelFakeBidNotice}>
                            <FormattedMessage
                              id="ListingPage.fakeBidNotice"
                              values={{
                                supportEmail: (
                                  <a
                                    href="mailto:support@groovebay.eu"
                                    className={css.fakeBidNoticeEmail}
                                  >
                                    support@groovebay.eu
                                  </a>
                                ),
                              }}
                            />
                          </div>
                        )}
                        {/* Normal active bid notice – shown when the user has a real pending bid */}
                        {!isOwnListing &&
                          isAuthenticated &&
                          !isBidPending &&
                          hasActiveBid &&
                          !hasFakeBid && (
                            <div className={css.biddingPanelLoginPrompt}>
                              <FormattedMessage id="ListingPage.activeBidNotice" />
                            </div>
                          )}
                        {!isOwnListing && !isAuthenticated && !isBidPending && (
                          <div className={css.biddingPanelLoginPrompt}>
                            <FormattedMessage id="ListingPage.loginToBid" />
                          </div>
                        )}
                        {!isOwnListing && isBidPending && (
                          <div className={css.listingPendingNotice}>
                            <FormattedMessage id="ListingPage.listingPendingBid" />
                          </div>
                        )}

                        <BidModal
                          isOpen={bidModalOpen}
                          onClose={() => setBidModalOpen(false)}
                          onManageDisableScrolling={onManageDisableScrolling}
                          onSubmitBid={({ bidAmountInSubunits, currency, message }) => {
                            onSendBid(currentListing, bidAmountInSubunits, currency, message)
                              .then(() => {
                                setBidModalOpen(false);
                                setTimeout(() => onFetchBids(listingId), 1500);
                              })
                              .catch(() => {});
                          }}
                          sendBidInProgress={sendBidInProgress}
                          sendBidError={sendBidError}
                          marketplaceCurrency={config.currency}
                          listingTitle={title}
                          minBidAmountInSubunits={(() => {
                            const activeBidAmounts = bids
                              .map(b => b?.attributes?.protectedData)
                              .filter(pd => pd?.isBidTransaction && pd?.bidStatus === 'pending')
                              .map(pd => pd?.bidAmountInSubunits)
                              .filter(a => typeof a === 'number');
                            const highestActiveBid = activeBidAmounts.length
                              ? Math.max(...activeBidAmounts)
                              : 0;
                            // Bid must be at least +1 in the major currency unit
                            // (e.g. +€1.00 => +100 subunits in EUR).
                            let minIncrementInSubunits = 1;
                            try {
                              minIncrementInSubunits = unitDivisor(config.currency);
                            } catch (e) {
                              // fall back to +1 subunit if currency config is missing
                            }
                            return highestActiveBid > 0
                              ? highestActiveBid + minIncrementInSubunits
                              : 1;
                          })()}
                        />

                        <UpdateBidModal
                          isOpen={updateBidModalOpen}
                          onClose={() => {
                            setUpdateBidModalOpen(false);
                            setBidToUpdate(null);
                          }}
                          onManageDisableScrolling={onManageDisableScrolling}
                          onSubmitBidUpdate={({ bidAmountInSubunits, currency, message }) => {
                            const transactionId = bidToUpdate?.id;
                            const previousAmount =
                              bidToUpdate?.attributes?.protectedData?.bidAmountInSubunits || 0;

                            onUpdateBid(
                              transactionId,
                              currentListing,
                              bidAmountInSubunits,
                              currency,
                              message
                            )
                              .then(() => {
                                // Store success data
                                setSuccessModalData({
                                  previousBidAmount: previousAmount,
                                  newBidAmount: bidAmountInSubunits,
                                });

                                // Close update modal and open success modal
                                setUpdateBidModalOpen(false);
                                setBidToUpdate(null);
                                setSuccessModalOpen(true);

                                // Refresh bids immediately to update UI
                                onFetchBids(listingId);
                              })
                              .catch(() => {
                                // Error handled in UpdateBidModal via updateBidError prop
                              });
                          }}
                          updateBidInProgress={updateBidInProgress}
                          updateBidError={updateBidError}
                          marketplaceCurrency={config.currency}
                          listingTitle={title}
                          currentBidAmount={
                            bidToUpdate?.attributes?.protectedData?.bidAmountInSubunits
                          }
                          highestBidAmount={(() => {
                            if (!bidToUpdate) return 0;

                            const currentBidderUuid =
                              bidToUpdate?.customer?.id?.uuid ||
                              bidToUpdate?.relationships?.customer?.data?.id?.uuid;

                            // Get all active bids excluding the current user's bid
                            const otherActiveBidAmounts = bids
                              .filter(b => {
                                const bidderUuid =
                                  b?.customer?.id?.uuid ||
                                  b?.relationships?.customer?.data?.id?.uuid;
                                return bidderUuid !== currentBidderUuid;
                              })
                              .map(b => b?.attributes?.protectedData)
                              .filter(pd => pd?.isBidTransaction && pd?.bidStatus === 'pending')
                              .map(pd => pd?.bidAmountInSubunits)
                              .filter(a => typeof a === 'number');

                            const highestOtherBid = otherActiveBidAmounts.length
                              ? Math.max(...otherActiveBidAmounts)
                              : 0;

                            return highestOtherBid;
                          })()}
                          minBidAmountInSubunits={(() => {
                            if (!bidToUpdate) return 1;

                            const currentBidderUuid =
                              bidToUpdate?.customer?.id?.uuid ||
                              bidToUpdate?.relationships?.customer?.data?.id?.uuid;

                            // Get all active bids excluding the current user's bid
                            const otherActiveBidAmounts = bids
                              .filter(b => {
                                const bidderUuid =
                                  b?.customer?.id?.uuid ||
                                  b?.relationships?.customer?.data?.id?.uuid;
                                return bidderUuid !== currentBidderUuid;
                              })
                              .map(b => b?.attributes?.protectedData)
                              .filter(pd => pd?.isBidTransaction && pd?.bidStatus === 'pending')
                              .map(pd => pd?.bidAmountInSubunits)
                              .filter(a => typeof a === 'number');

                            const highestOtherBid = otherActiveBidAmounts.length
                              ? Math.max(...otherActiveBidAmounts)
                              : 0;

                            // Return just the highest bid amount (validation will handle +1)
                            return highestOtherBid > 0 ? highestOtherBid : 0;
                          })()}
                          transactionId={bidToUpdate?.id}
                        />

                        <BidUpdateSuccessModal
                          isOpen={successModalOpen}
                          onClose={() => setSuccessModalOpen(false)}
                          onManageDisableScrolling={onManageDisableScrolling}
                          previousBidAmount={successModalData.previousBidAmount}
                          newBidAmount={successModalData.newBidAmount}
                          marketplaceCurrency={config.currency}
                          intl={intl}
                        />
                      </div>
                    );
                  }

                  return (
                    <OrderPanel
                      className={classNames(css.productOrderPanel, {
                        [css.imagesEnabled]: showListingImage,
                        [css.bothPurchaseTypesEnabled]:
                          publicData?.biddingEnabled && publicData?.fixedPriceEnabled,
                      })}
                      listing={currentListing}
                      isOwnListing={isOwnListing}
                      onSubmit={handleOrderSubmit}
                      authorLink={
                        <NamedLink
                          className={css.authorNameLink}
                          name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                          params={params}
                          to={{ hash: '#author' }}
                        >
                          {authorDisplayName}
                        </NamedLink>
                      }
                      title={
                        <FormattedMessage
                          id="ListingPage.orderTitle"
                          values={{ title: richTitle }}
                        />
                      }
                      titleDesktop={
                        <H4 as="h1" className={css.orderPanelTitle}>
                          <FormattedMessage
                            id="ListingPage.orderTitle"
                            values={{ title: richTitle }}
                          />
                        </H4>
                      }
                      payoutDetailsWarning={payoutDetailsWarning}
                      author={ensuredAuthor}
                      onManageDisableScrolling={onManageDisableScrolling}
                      onContactUser={onContactUser}
                      purchaseType={purchaseType}
                      onPurchaseTypeChange={setPurchaseType}
                      adjustedStock={adjustedStock}
                      {...restOfProps}
                      validListingTypes={config.listing.listingTypes}
                      marketplaceCurrency={config.currency}
                      dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                      marketplaceName={config.marketplaceName}
                      showListingImage={showListingImage}
                    />
                  );
                })()}

                <SectionAuthorMaybe
                  title={title}
                  listing={currentListing}
                  authorDisplayName={authorDisplayName}
                  onContactUser={onContactUser}
                  isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                  onCloseInquiryModal={() => setInquiryModalOpen(false)}
                  sendInquiryError={sendInquiryError}
                  sendInquiryInProgress={sendInquiryInProgress}
                  onSubmitInquiry={onSubmitInquiry}
                  currentUser={currentUser}
                  onManageDisableScrolling={onManageDisableScrolling}
                  reviewCount={authorReviewCount}
                  averageRating={authorAverageRating}
                />
                {/* <div className={css.shareBtn}>
                <IconCard brand='share' />
                Share
              </div> */}
              </div>
            ) : null}
          </div>
        </div>
        <div className={css.listingCarouselWrapper}>
          <div className={css.listingCarouselContainer}>
            {Array.isArray(listings) && listings?.length > 0 ? (
              <div>
                <div className={css.listingHeading}>
                  <div className={css.title}>
                    <FormattedMessage id="ListingPage.exploreMoreListings" />
                  </div>

                  <NamedLink name="SearchPage" className={css.carouselViewAllBtn}>
                    <FormattedMessage id="ListingPage.viewMoreListings" />
                    <IconCard brand="arrowLineArrow" />
                  </NamedLink>
                </div>

                <ListingCarousel
                  id={'listing-carousel'}
                  listings={listings}
                  slidesToShow={slidesToShow}
                  showArrows={listings.length > slidesToShow}
                  showDots={false}
                  autoplay={false}
                  emblaSettings={{ loop: false, align: 'start' }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    fetchReviewsInProgress,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    fetchReviewsInProgress,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
    authorReviews: state.ListingPage.authorReviews,
    recentListings: recentListingsSelector(state),
    bids: state.ListingPage.bids,
    fetchBidsInProgress: state.ListingPage.fetchBidsInProgress,
    sendBidInProgress: state.ListingPage.sendBidInProgress,
    sendBidError: state.ListingPage.sendBidError,
    acceptBidInProgress: state.ListingPage.acceptBidInProgress,
    acceptBidError: state.ListingPage.acceptBidError,
    rejectBidInProgress: state.ListingPage.rejectBidInProgress,
    updateBidInProgress: state.ListingPage.updateBidInProgress,
    updateBidError: state.ListingPage.updateBidError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
  onRecentListingsSelector: arg => dispatch(searchRecentListings(arg)),
  onFetchBids: listingId => dispatch(fetchBids(listingId)),
  onSendBid: (listing, bidAmountInSubunits, currency, message) =>
    dispatch(sendBid(listing, bidAmountInSubunits, currency, message)),
  onAcceptBid: (transactionId, listingId) => dispatch(acceptBid(transactionId, listingId)),
  onRejectBid: transactionId => dispatch(rejectBid(transactionId)),
  onUpdateBid: (transactionId, listing, bidAmountInSubunits, currency, message) =>
    dispatch(updateBid(transactionId, listing, bidAmountInSubunits, currency, message)),
  onRefreshListing: (listingId, config) => dispatch(showListing(listingId, config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
